/**
 * Resolves what a builder session is actually building — the entity, its type,
 * and the agent's latest proposed version of it.
 *
 * ## The gap this closes
 *
 * A builder conversation records its subject on the conversation row, which
 * works on the edit path because the CTA already knows the record. On the
 * CREATE path there is no id to record: the create actions write no CRM row
 * before `execute()`, so the id is minted inside the agent's `transform()` and
 * lives only in the proposal payload. Nothing writes it back to the
 * conversation — so without this the panel would sit on its "your automation
 * will appear here" placeholder through the whole authoring session.
 *
 * Reading the id from the proposal closes that gap and removes the need for a
 * write-back at all.
 *
 * ## Why the proposal payload is the render source, not the saved entity
 *
 * Until a proposal is approved the entity does not exist, so the saved record
 * has nothing to show. The payload, by contrast, carries the whole automation —
 * script, fixtures, triggers, documentation — from the first turn.
 *
 * The payload reaches the panel as PREVIEW OVERLAYS — the same derivation
 * (`derivePreviewOverlays`) and the same `PreviewOverlay` shape the action
 * center publishes, so the record surface projects a builder draft exactly as
 * it projects any other pending proposal. Nothing here knows the payload
 * shapes; that lives in the shared derivation.
 *
 * `entity_id` is what makes this safe to key on: it is minted exactly once in
 * `transform()` and frozen thereafter (the payload models carry a "Never add a
 * default here" invariant, because re-minting on read-back would destroy
 * idempotency), and it IS the id the entity gets at approval. So one key spans
 * both phases — render from the payload now, and the CRM store starts answering
 * for the same id afterwards, with no remount.
 *
 * ## Freshness
 *
 * The draft advances once per completed turn, not mid-chain. Proposal websocket
 * traffic is suppressed twice over for this case: `proposal.updated` fires only
 * on resolution transitions (a payload edit carries the resolution forward
 * unchanged), and chat-built proposals are excluded from the broadcast entirely
 * because `ahoy_chat` is an interactive channel. Callers get a new draft when
 * the conversation reloads, which is the same cadence the inline proposal cards
 * already use.
 */

import { useQuery } from '@tanstack/react-query';
import { useEffect, useMemo } from 'react';

import type { PreviewOverlay } from '@/lib/crm/preview';
import { logger } from '@/lib/logger';
import { useReverseAssociationTypeResolver } from '@/lib/proposals/hooks/useReverseAssociationTypeResolver';
import { proposalDetailQuery } from '@/lib/proposals/query/queries';
import { derivePreviewOverlays } from '@/lib/proposals/utils/derivePreviewOverlays';

import type { BuilderDraft } from './useBuilderSubject.helpers';
import { subjectEntityIdOf } from './useBuilderSubject.helpers';

const EMPTY_OVERLAYS: Record<string, PreviewOverlay> = {};

/**
 * Every action in a builder proposal is approved or rejected together — there
 * is no per-action selection, so every contribution is "selected".
 */
const SELECT_ALL = () => true;

export interface BuilderSubject {
  /**
   * The entity being built, stable across the proposal→entity transition.
   *
   * `undefined` only before the agent's first proposal on the create path —
   * a normal state, not an error.
   */
  entityId: string | undefined;
  /**
   * The kind of thing being built. The agent's proposed payload shape wins
   * while a proposal is pending; the conversation's own recorded type is the
   * floor, so this is set from the first render and — unlike the draft —
   * survives approval.
   *
   * `undefined` only when the caller has no conversation type to pin, which in
   * practice means a caller outside a builder conversation.
   */
  entityTypeName: string | undefined;
  /** The agent's latest proposed version, absent until it proposes one. */
  draft: BuilderDraft | undefined;
  /**
   * Preview overlays for EVERY entity the pending proposal touches, keyed by
   * entity id — the subject and anything created or edited alongside it. The
   * host publishes these through `CrmPreviewProvider` so the record surface
   * (and every chip and badge under it) projects the whole proposal, not only
   * its subject. Empty when nothing is pending.
   */
  overlays: Record<string, PreviewOverlay>;
  /** True while the proposal detail is in flight. */
  isLoading: boolean;
  /**
   * True when the proposal detail fetch itself failed — a network error, a
   * server error, anything short of a successful response. Distinct from
   * "no proposal yet" (which is `draft: undefined` with `isError: false`):
   * a caller MUST render a distinct, retryable error state here rather than
   * falling through to the ambiguous empty state, or a real failure looks
   * identical to the normal pre-first-proposal window.
   */
  isError: boolean;
  /** The error behind `isError`, for a caller that wants to display it. */
  error: unknown;
  /** Retries the proposal detail fetch after a failure. */
  refetch: () => void;
  /**
   * The most recent proposal in `proposalIds`, ignoring any
   * `selectedProposalId` override. Exposed so a caller can tell when the
   * agent has issued a new proposal — the moment a prior selection should be
   * dropped, since pinning the panel to an older proposal would hide the
   * agent's newest answer.
   */
  mostRecentProposalId: number | undefined;
}

export interface UseBuilderSubjectArgs {
  orgId: number | undefined;
  /**
   * Proposals created in this conversation, oldest first — the shape
   * `created_proposals` already delivers on each reasoning step. The LAST
   * element is treated as most recent, matching the backend's
   * `_resolve_builder_subject_id`, which sorts by `created_at` descending —
   * this is the one place that ordering is assumed, so keep it in step with
   * however `proposalIds` is built (chains → steps → `created_proposals`,
   * all three already chronological). The newest wins by default: the agent
   * is asked to revise by issuing a fresh proposal rather than editing the
   * existing one, so the newest is normally the current draft — unless a
   * caller overrides it with `selectedProposalId`.
   */
  proposalIds: readonly number[];
  /**
   * The subject recorded on the conversation. Authoritative when present,
   * which is the edit path — on the create path it is null, because the id is
   * not minted until the agent's first proposal.
   */
  pinnedEntityId: string | undefined;
  /**
   * The entity type recorded on the conversation. Unlike the subject id this is
   * set from the moment the conversation is created, on both paths — a builder
   * conversation always knows what KIND of record it is building.
   *
   * It is the only durable source. The draft's type comes from the proposal
   * payload, and the draft is deliberately dropped once the proposal resolves
   * (see below), so without this the panel forgets what it just built the
   * instant the user approves.
   */
  pinnedEntityTypeName: string | undefined;
  /**
   * A caller-supplied override for which proposal drives the panel — the
   * user's click on an inline proposal card, when this conversation has more
   * than one. `undefined` (the default) falls back to the most recent
   * proposal in `proposalIds`, which is the entire pre-existing behavior:
   * this param only ever narrows a many-proposal conversation down to one,
   * it never changes what a single-proposal conversation renders.
   *
   * A value that is not in `proposalIds` is ignored, not honoured — the
   * selected proposal was withdrawn and the server no longer lists it. The
   * hook falls back to the most recent candidate rather than rendering a
   * proposal that has no card left to un-click.
   */
  selectedProposalId?: number | undefined;
}

export function useBuilderSubject({
  orgId,
  proposalIds,
  pinnedEntityId,
  pinnedEntityTypeName,
  selectedProposalId,
}: UseBuilderSubjectArgs): BuilderSubject {
  const mostRecentProposalId =
    proposalIds.length > 0 ? proposalIds[proposalIds.length - 1] : undefined;
  // A selection only counts while it still names a candidate. The server drops
  // a withdrawn proposal from `created_proposals`, so a selection can outlive
  // its card: honouring it would fetch the withdrawn proposal, produce no
  // draft, and leave an empty panel with nothing left to un-click. Falling
  // through to the most recent candidate is the same default the caller gets
  // with no selection at all.
  const effectiveSelectedProposalId =
    selectedProposalId !== undefined && proposalIds.includes(selectedProposalId)
      ? selectedProposalId
      : undefined;
  const latestProposalId = effectiveSelectedProposalId ?? mostRecentProposalId;

  const canFetch = orgId !== undefined && latestProposalId !== undefined;
  const {
    data: proposal,
    isLoading,
    isError,
    error,
    refetch,
  } = useQuery({
    ...proposalDetailQuery(orgId ?? 0, latestProposalId ?? 0),
    enabled: canFetch,
  });

  // A fetch failure otherwise leaves no trace: the UI degrades to the same
  // "nothing proposed yet" empty state a normal opening render shows, so
  // without this the only signal a real failure ever left was in the network
  // tab. `canFetch` gates it because `isError` only means something once
  // we've actually asked — an unmounted/disabled query reports `isError:
  // false`, but gating keeps this in step with the same guard `isLoading`
  // uses below.
  useEffect(() => {
    if (!canFetch || !isError) {
      return;
    }
    logger.warn('Failed to fetch proposal detail for builder subject', {
      orgId,
      proposalId: latestProposalId,
      error: String(error),
    });
  }, [canFetch, isError, error, orgId, latestProposalId]);

  const resolveReverseAssociationTypeName =
    useReverseAssociationTypeResolver(orgId);

  return useMemo(() => {
    // Only a PENDING proposal is a draft. Once it resolves, what it described
    // either exists on the record or was discarded — either way the record is
    // now the truth, and continuing to render the payload would leave the panel
    // saying "Draft" with a Save button over an entity that is already saved.
    const isPending = proposal?.resolution === 'pending';
    // One derivation for every surface that projects a proposal. Folding the
    // actions per entity is what makes "rename it and update the summary" —
    // two events on the same automation — render as ONE draft with both
    // changes, in execution order, last write winning per property.
    const overlays =
      isPending && proposal.actions && proposal.actions.length > 0
        ? derivePreviewOverlays(
            proposal.actions,
            SELECT_ALL,
            resolveReverseAssociationTypeName
          )
        : EMPTY_OVERLAYS;
    // The id can still come from the proposal after it resolves. Approval now
    // writes the minted id onto the conversation, so `pinnedEntityId` usually
    // wins below — but this covers the window before that write lands, and any
    // conversation approved before it existed.
    //
    // Deliberately NOT filtered to approved, unlike the backend's
    // `_resolve_builder_subject_id`. The backend must filter because its
    // consumer is a prompt that asserts the record EXISTS; here the id only
    // feeds the record surface, and a miss degrades to the draft — the panel
    // renders the projection instead of claiming something untrue. The create
    // path needs the minted id while the proposal is still pending (see
    // "recovers the minted id from the proposal payload").
    const resolvedEntityId = subjectEntityIdOf(proposal?.actions);
    // The subject's overlay, when the proposal is pending. Falls back to the
    // first derived entity for the (theoretical) case where the first action
    // targets an entity the derivation produced no overlay for.
    const subjectOverlay =
      (resolvedEntityId ? overlays[resolvedEntityId] : undefined) ??
      Object.values(overlays)[0];
    const draft: BuilderDraft | undefined =
      isPending && proposal && subjectOverlay
        ? { proposalId: proposal.id, overlay: subjectOverlay }
        : undefined;

    return {
      // The pinned id wins when present: on the edit path the conversation
      // names the record the user opened, which is more stable than whichever
      // proposal happens to be newest.
      entityId: pinnedEntityId ?? resolvedEntityId,
      // The draft's type is the live one while a proposal is pending, so it
      // wins — a proposal that changes the type should be reflected before it
      // is saved. The conversation's type is the floor: it survives approval,
      // which the draft deliberately does not, so the panel keeps knowing what
      // kind of record it is looking at once the build is done.
      entityTypeName: draft?.overlay.entityTypeName ?? pinnedEntityTypeName,
      draft,
      overlays,
      isLoading: canFetch && isLoading,
      isError: canFetch && isError,
      error,
      refetch,
      mostRecentProposalId,
    };
  }, [
    proposal,
    pinnedEntityId,
    pinnedEntityTypeName,
    resolveReverseAssociationTypeName,
    canFetch,
    isLoading,
    isError,
    error,
    refetch,
    mostRecentProposalId,
  ]);
}
